<script>
// layout
import Authenticated from "@/Layouts/Authenticated";
// components
import SupplierTabs from './SupplierTabs.vue';
import Pagination from "@/Components/Pagination";
import SearchFilter from "@/Components/SearchFilter";
// vue packages
import moment from "moment/moment";
import pickBy from "lodash/pickBy";
import debounce from "lodash/debounce";
import mapValues from "lodash/mapValues";
import {DownloadIcon} from "@heroicons/vue/outline";
import {Link, Head, usePage, useForm} from "@inertiajs/vue3";

export default {
    layout: Authenticated,
    components: {
        Head,
        Link,
        Pagination,
        SearchFilter,
        DownloadIcon,
        SupplierTabs,
    },
    props: {
        organisation: Object,
        quotes: Array,
        filters: Object
    },
    created() {
        this.moment = moment;
    },
    data() {
        return {
            form: useForm({
                search: null,
                filter: {
                    starts_at: this.filters.starts_at,
                    ends_at: this.filters.ends_at,
                }
            })
        }
    },
    methods: {
        reset() {
            this.form.search = null;
        }
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route('frontend.organisations.suppliers.organisation.fixtures.show', this.organisation.id), pickBy(this.form), { preserveState: true, preserveScroll: true });
            }, 500),
        },
    },
}
</script>
<template>
    <Head :title="organisation.name"/>
    <ValidationErrors :show="showErrors"/>
    <div class="w-full space-y-3 mb-6">
        <div>
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                <span
                    v-if="organisation.name === null"
                    v-html="'Create New ' + organisation.type.charAt(0).toUpperCase() + organisation.type.substring(1)">
                </span>
                <span v-else v-html="organisation.name"></span>
            </h1>
            <h2 v-if="organisation.registered_legal_name === null"
                class="mb-2 mt-0 text-md font-medium leading-7 text-gray-500 sm:truncate"
                v-html="''"></h2>
            <h2 v-else class="mb-2 mt-0 text-md font-medium leading-7 text-gray-500 sm:truncate"
                v-html="organisation.registered_legal_name"></h2>
        </div>
        <div class="space-y-6 mt-2 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <SupplierTabs v-if="organisation" :organisation="organisation"/>
                <div class="shadow sm:rounded-md sm:overflow-hidden">
                    <div class="bg-white px-4 p-6 space-y-6">
                        <div id="supplier_form" class="space-y-6">
                            <div class="relative">
                                <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div class="w-full border-t border-gray-300"/>
                                </div>
                                <div class="relative flex items-center justify-between">
                                    <span class="pr-3 bg-white text-lg font-bold text-gray-900">Fixtures</span>
                                    <a :href="route('frontend.organisations.suppliers.organisation.fixtures.export', {
                                            organisation: organisation.id,
                                            starts_at: form.filter.starts_at ?? null,
                                            ends_at: form.filter.ends_at ?? null,
                                            search: form.search ?? null
                                        })"
                                        class="inline-flex items-center shadow-sm px-4 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                        <DownloadIcon class="-ml-1.5 mr-1 h-5 w-5 text-gray-400" aria-hidden="true"/>
                                        <span>Export All</span>
                                    </a>
                                </div>
                            </div>
                            <div class="w-full flex gap-3 justify-between">
                                <div class="flex gap-1 w-full items-center">
                                    <div class="flex items-center gap-2 w-full">
                                        <input v-model="form.filter.starts_at" type="date"
                                            name="starts_at" id="starts_at"
                                            autocomplete="starts_at"
                                            class="flex-grow-1 mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="flex items-center gap-2 w-full">
                                        <input v-model="form.filter.ends_at" type="date"
                                            name="ends_at" id="ends_at"
                                            autocomplete="ends_at"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                    <div class="flex items-center gap-2 w-full">
                                        <search-filter :show-filter="false" v-model:value="form.search" class="w-full" @reset="reset"></search-filter>
                                    </div>
                                </div>
                            </div>
                            <div v-if="quotes.data.length > 0">
                                <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6">
                                    <div class="flex flex-col mt-3 space-y-3">
                                        <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                            <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                                <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                                    <table class="min-w-full divide-y divide-gray-200">
                                                        <thead>
                                                            <tr>
                                                                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">Reference</th>
                                                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Name</th>
                                                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Quote Name</th>
                                                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Travel Manager</th>
                                                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Confirmation Date</th>
                                                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                                                                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody class="bg-white">
                                                            <tr v-for="quote in quotes.data" :key="quote.id" class="even:bg-gray-50">
                                                                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">{{ quote?.fixture.reference }}</td>
                                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ quote?.fixture.title }}</td>
                                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ quote?.name }}</td>
                                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ quote?.fixture.travel_manager?.name }}</td>
                                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                    <span v-if="quote.confirmed_at">{{ moment(quote.confirmed_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}</span>
                                                                </td>
                                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    <span v-if="quote?.fixture.status === 'Request'" class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-500 text-white">
                                                                        {{ quote?.fixture.status }}
                                                                    </span>
                                                                    <span v-if="quote?.fixture.status === 'Cost'" class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-yellow-100 text-yellow-800">
                                                                        {{ quote?.fixture.status }}
                                                                    </span>
                                                                    <span v-if="quote?.fixture.status === 'Cancelled'" class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-red-100 text-red-800">
                                                                        {{ quote?.fixture.status }}
                                                                    </span>
                                                                    <span v-if="quote?.fixture.status === 'Complete'" class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-green-100 text-green-800">
                                                                        {{ quote?.fixture.status }}
                                                                    </span>
                                                                </td>
                                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                    <Link :href="route('frontend.fixture.quote.show', [quote?.fixture.id, quote.id])" class="underline">View Quote</Link>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                        <Pagination :links="quotes.links"/>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="w-full text-center py-5">
                                There are currently no fixtures between {{ moment(this.form.filter.starts_at).format('Do MMM YYYY') }} and {{ moment(this.form.filter.ends_at).format('Do MMM YYYY') }}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>
